import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useDispatch } from 'react-redux';
import Ionicons from '@react-native-vector-icons/ionicons';
import { useAppSelector } from '../../../redux/helper';
import {
  clearSimplifiedProductDraft,
  selectSimplifiedProductDraft,
  updateSimplifiedProductDraft,
} from '../../../redux/ownerActivationSlice';
import { dispatchOwnerActivationEvent } from '../../../services/ownerActivationEvents';
import ownerActivationAnalytics from '../../../services/ownerActivationAnalytics';
import type { OwnerActivationAnalyticsPlatform } from '../../../services/ownerActivationAnalyticsTypes';
import { Platform } from 'react-native';
import { GTFonts, useTheme, type Theme } from '../../../theme';
import { fs, iconSize, s, v } from '../../../utils/libs';
import { AppAlert } from '../../../services/AppAlert';
import {
  ProductFormProvider,
  useProductFormState,
} from '../../products/newproducts/ProductFormProvider';
import { useCreateProductForm } from '../../products/newproducts/useCreateProductForm';
import { validateProductForm } from '../../products/newproducts/productFormValidation';
import type { ProductFormErrors } from '../../products/newproducts/productFormTypes';
import {
  ProductDetailsFields,
  SimplePricingInventoryFields,
} from '../../products/newproducts/components/SimpleProductFields';
import {
  ServiceChargeTiles,
  ServicePricingFields,
} from '../../products/newproducts/components/ServiceProductFields';
import CategorySelectModal from '../../products/newproducts/components/CategorySelectModal';
import {
  getServiceChargeType,
  getServicePriceType,
  getServiceUnit,
  type ProductKind,
  type ServiceChargeType,
} from '../../../utils/productKind';
import { getPreloadCategoriesForBusinessType } from '../../../utils/preloadCategoriesByBusinessType';
import {
  addCustomCategory,
  loadCustomCategories,
  mergeCategoryOptions,
} from '../../../utils/productCatalogStorage';
import { selectUniqueCategories } from '../../../redux/productSlice';
import SetupWizardShell from './SetupWizardShell';
import {
  evaluateFirstProductLocationGuard,
  describeFirstProductGuardBlock,
} from '../firstProductLocationGuard';
import {
  draftToFormSeed,
  draftToItemTypeSeed,
  draftValuesDiffer,
  formToDraftValues,
  hasDraftContent,
} from '../firstProductDraft';
import { getFirstProductStepCopy } from '../firstProductCopy';
import { useFirstProductExistingData } from '../useFirstProductExistingData';

/**
 * Wizard first-product step (US3, T054–T059). Simplified product form
 * (reusing NewProduct field components), a verified real-location guard,
 * existing-product detection, persisted draft, and the PRODUCT_CREATED
 * milestone. A line under the intro switches the form between a product (the
 * default) and the Add Product service fields, without the photo. Renders its
 * own {@link SetupWizardShell} so it can own the primary/secondary action +
 * busy state.
 */

export interface FirstProductStepProps {
  stepIndex: number;
  totalSteps: number;
  onBack?: () => void;
  /** Global "Skip setup" (post-Step-1). */
  onSkipSetup?: () => void;
  /** Inline "Skip product for now" → advance without creating. */
  onSkipProduct: () => void;
  /** Advance to the next step after create or existing-product satisfaction. */
  onProductSatisfied: () => void;
  businessType?: string;
}

const FirstProductStepInner: React.FC<
  FirstProductStepProps & {
    /** The Type the step opens on (from the persisted draft). */
    initialKind: ProductKind;
  }
> = ({
  stepIndex,
  totalSteps,
  onBack,
  onSkipSetup,
  onSkipProduct,
  onProductSatisfied,
  businessType,
  initialKind,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const dispatch = useDispatch();
  const form = useProductFormState();
  // Product or service, switched from the line under the intro; a service is
  // neither simple nor variant
  const [kind, setKind] = useState<ProductKind>(initialKind);
  const isService = kind === 'service';
  const copy = getFirstProductStepCopy(kind);
  const serviceChargeType = getServiceChargeType(form.priceType);

  const currentLocation = useAppSelector(
    state => state.location.currentLocation,
  );
  const locationLoading = useAppSelector(state => state.location.isLoading);

  const guard = useMemo(
    () =>
      evaluateFirstProductLocationGuard({
        currentLocation,
        isLoading: locationLoading,
      }),
    [currentLocation, locationLoading],
  );

  const { evidence, refetch } = useFirstProductExistingData(guard);

  const analyticsPlatform = (): OwnerActivationAnalyticsPlatform =>
    Platform.OS === 'ios'
      ? 'ios'
      : Platform.OS === 'android'
        ? 'android'
        : 'web';

  const productStepProps = useCallback(
    () => ({
      onboarding_version: 1 as const,
      platform: analyticsPlatform(),
      step_id: 'first_product' as const,
      step_index: stepIndex,
      product_mode: (isService
        ? 'service'
        : form.hasVariants
          ? 'variants'
          : 'simple') as 'simple' | 'variants' | 'service',
    }),
    [form.hasVariants, isService, stepIndex],
  );

  const productStartedRef = useRef(false);
  useEffect(() => {
    if (productStartedRef.current) return;
    productStartedRef.current = true;
    try {
      ownerActivationAnalytics.track(
        'owner_setup_product_started',
        productStepProps(),
      );
    } catch {
      // ignore
    }
  }, [productStepProps]);

  const existingDetectedRef = useRef(false);
  useEffect(() => {
    if (evidence.kind !== 'satisfied' || existingDetectedRef.current) return;
    existingDetectedRef.current = true;
    try {
      ownerActivationAnalytics.track('owner_setup_existing_product_detected', {
        ...productStepProps(),
        product_source: 'existing',
      });
    } catch {
      // ignore
    }
  }, [evidence.kind, productStepProps]);

  const [errors, setErrors] = useState<ProductFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [addAnother, setAddAnother] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const employee = useAppSelector(
    state => state.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const productCategoriesFromStore = useAppSelector(selectUniqueCategories);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const categories = useMemo(
    () =>
      mergeCategoryOptions(
        getPreloadCategoriesForBusinessType(businessType),
        productCategoriesFromStore,
        customCategories,
      ),
    [businessType, productCategoriesFromStore, customCategories],
  );
  useEffect(() => {
    let alive = true;
    (async () => {
      const cats = await loadCustomCategories(ownerId);
      if (!alive) return;
      setCustomCategories(cats);
    })();
    return () => {
      alive = false;
    };
  }, [ownerId]);

  // Persist the simplified draft and its Type as the user types (contract §9).
  const lastPersistedRef = useRef(
    formToDraftValues(form.formData, { kind, priceType: form.priceType }),
  );
  useEffect(() => {
    const next = formToDraftValues(form.formData, {
      kind,
      priceType: form.priceType,
    });
    if (!draftValuesDiffer(lastPersistedRef.current, next)) return;
    lastPersistedRef.current = next;
    if (!hasDraftContent(next)) return;
    dispatch(
      updateSimplifiedProductDraft({
        ...next,
        updatedAt: new Date().toISOString(),
      }),
    );
  }, [dispatch, form.formData, form.priceType, kind]);

  const { submit, submitting } = useCreateProductForm({
    source: 'owner_setup',
    onCreated: async context => {
      dispatchOwnerActivationEvent(dispatch, {
        type: 'PRODUCT_CREATED',
        occurredAt: new Date().toISOString(),
        source: 'owner_setup',
        locationId: context.locationId,
        locationType: 'real',
        savedProductIds: context.savedProductIds,
      });
      dispatch(clearSimplifiedProductDraft());
      try {
        ownerActivationAnalytics.track('owner_setup_product_created', {
          ...productStepProps(),
          product_source: 'created',
          refresh_status: context.refreshStatus,
          completion_source: 'wizard_product_create',
        });
      } catch {
        // ignore
      }
    },
  });

  const existingSatisfied =
    evidence.kind === 'satisfied' && !addAnother;

  const handleChangeField = useCallback(
    (field: string, value: string) => {
      form.setField(field as any, value);
      if (errors[field as keyof ProductFormErrors]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    },
    [errors, form],
  );

  // Switching Type keeps what was typed and starts a service on Per service,
  // as the Add Product form does
  const handleChangeKind = useCallback(
    (nextKind: ProductKind) => {
      setKind(nextKind);
      form.setPriceType('item');
      form.setFormData(prev => ({ ...prev, mass: '', unitofmass: '' }));
      // The other form's errors no longer match the fields on screen
      setErrors({});
      setFormError(null);
    },
    [form],
  );

  // Per hour is a per-unit item whose unit is the hour
  const handleChangeServiceCharge = useCallback(
    (charge: ServiceChargeType) => {
      form.setPriceType(getServicePriceType(charge));
      form.setFormData(prev => ({
        ...prev,
        mass: '',
        unitofmass: getServiceUnit(charge),
      }));
    },
    [form],
  );

  const runSubmit = useCallback(async () => {
    setFormError(null);
    try {
      ownerActivationAnalytics.track(
        'owner_setup_product_submit_attempted',
        productStepProps(),
      );
    } catch {
      // ignore
    }
    // Pre-validate for inline field errors before hitting the network.
    const validation = validateProductForm({
      formData: form.formData,
      priceType: form.priceType,
      hasVariants: form.hasVariants,
      variantValidationError: form.variantValidationError,
      isService,
    });
    if (!validation.valid) {
      if (validation.formError) {
        setFormError(validation.formError);
      } else {
        setErrors(validation.fieldErrors);
      }
      try {
        ownerActivationAnalytics.track('owner_setup_product_failed', {
          ...productStepProps(),
          error_category: 'validation',
        });
      } catch {
        // ignore
      }
      return;
    }
    setErrors({});

    const outcome = await submit({
      formData: form.formData,
      priceType: form.priceType,
      hasVariants: form.hasVariants,
      variantAttributeNames: form.variantAttributeNames,
      variantRows: form.variantRows,
      isService,
    });

    if (outcome.kind === 'invalid') {
      if (outcome.formError) {
        setFormError(outcome.formError);
      } else {
        setErrors(outcome.fieldErrors);
      }
      try {
        ownerActivationAnalytics.track('owner_setup_product_failed', {
          ...productStepProps(),
          error_category: 'validation',
        });
      } catch {
        // ignore
      }
      return;
    }
    if (outcome.kind === 'duplicate_or_unsaved') {
      // Retain values; the product already exists in inventory.
      setFormError(outcome.message);
      try {
        ownerActivationAnalytics.track('owner_setup_product_failed', {
          ...productStepProps(),
          error_category: 'duplicate',
        });
      } catch {
        // ignore
      }
      return;
    }
    if (outcome.kind === 'created') {
      form.didSubmitRef.current = true;
      const productName =
        String(form.formData.productname || '').trim() || copy.fallbackName;
      // Success is authoritative even if list refresh fails (retried on Home).
      const message =
        outcome.refreshStatus === 'failed'
          ? `"${productName}" was saved. We could not refresh your list right now, but it will appear shortly.`
          : `"${productName}" has been added to your inventory.`;
      AppAlert.alert(copy.createdTitle, message, [
        { text: 'Continue', onPress: () => onProductSatisfied() },
      ]);
      return;
    }
    // failed
    setFormError(outcome.userMessage);
    const categoryMap = {
      offline: 'network',
      validation: 'validation',
      authentication: 'authentication',
      location: 'location_unavailable',
      server: 'server',
      unknown: 'unknown',
    } as const;
    try {
      ownerActivationAnalytics.track('owner_setup_product_failed', {
        ...productStepProps(),
        error_category: categoryMap[outcome.category] ?? 'unknown',
      });
    } catch {
      // ignore
    }
  }, [copy, form, isService, onProductSatisfied, productStepProps, submit]);

  // ----- Location guard blocks the whole step -----
  if (guard.kind !== 'ready') {
    const block = describeFirstProductGuardBlock(guard);
    return (
      <SetupWizardShell
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        eyebrow="Quick setup"
        heading={copy.heading}
        onBack={onBack}
        onSkip={onSkipSetup}
        primaryLabel={copy.skipLabel}
        onPrimaryPress={onSkipProduct}
        testID="wizard-step-first-product">
        <View style={styles.guardCard} testID="first-product-guard">
          {guard.kind === 'loading' ? (
            <ActivityIndicator color={theme.colors.primary} />
          ) : (
            <Ionicons
              name="alert-circle-outline"
              size={iconSize(28)}
              color={theme.colors.warningDark}
            />
          )}
          <Text style={styles.guardTitle}>{block.title}</Text>
          <Text style={styles.guardBody}>{block.body}</Text>
        </View>
      </SetupWizardShell>
    );
  }

  // ----- Existing real product already satisfies the step -----
  if (existingSatisfied) {
    return (
      <SetupWizardShell
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        eyebrow="Quick setup"
        heading="You already have products"
        supportingCopy={`Great — ${currentLocation?.bp_location_name || 'your location'} already has products, so this step is done.`}
        onBack={onBack}
        onSkip={onSkipSetup}
        primaryLabel="Continue"
        onPrimaryPress={onProductSatisfied}
        secondaryLabel="Add another product"
        onSecondaryPress={() => setAddAnother(true)}
        secondaryAccessibilityHint="Add one more product before continuing"
        testID="wizard-step-first-product">
        <View style={styles.existingCard} testID="first-product-existing">
          <Ionicons
            name="checkmark-circle"
            size={iconSize(28)}
            color={theme.colors.success}
          />
          <Text style={styles.guardBody}>
            You can always add more products later from Back Office or Getting
            Started on Home.
          </Text>
        </View>
      </SetupWizardShell>
    );
  }

  // ----- Product form (simplified fields only) -----
  return (
    <>
    <SetupWizardShell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      eyebrow="Quick setup"
      heading={copy.heading}
      supportingCopy={copy.supportingCopy}
      supportingAction={{
        prompt: copy.switchPrompt,
        label: copy.switchLabel,
        onPress: () => handleChangeKind(isService ? 'product' : 'service'),
      }}
      onBack={onBack}
      onSkip={onSkipSetup}
      primaryLabel={copy.primaryLabel}
      onPrimaryPress={runSubmit}
      primaryBusy={submitting}
      primaryDisabled={form.isImageProcessing}
      secondaryLabel={copy.skipLabel}
      onSecondaryPress={onSkipProduct}
      secondaryAccessibilityHint={copy.skipAccessibilityHint}
      testID="wizard-step-first-product">
      <View style={styles.formContent} testID="first-product-form">
        <ProductDetailsFields
          formData={form.formData}
          errors={errors}
          onChangeField={handleChangeField}
          onOpenCategoryPicker={() => setCategoryModalVisible(true)}
          showSectionTitle={false}
          nameLabel={isService ? 'Service Name *' : undefined}
        />

        {isService ? (
          <>
            <ServiceChargeTiles
              chargeType={serviceChargeType}
              onChange={handleChangeServiceCharge}
            />
            <ServicePricingFields
              formData={form.formData}
              errors={errors}
              chargeType={serviceChargeType}
              onChangeField={handleChangeField}
            />
          </>
        ) : (
          <SimplePricingInventoryFields
            formData={form.formData}
            errors={errors}
            priceType={form.priceType}
            unitofmass={form.formData.unitofmass}
            onChangeField={handleChangeField}
            showMinimumStockLevel={false}
            showSectionTitles={false}
          />
        )}

        {formError ? (
          <View style={styles.errorRow} testID="first-product-error">
            <Ionicons
              name="alert-circle-outline"
              size={iconSize(16)}
              color={theme.colors.errorDark}
            />
            <Text style={styles.errorText}>{formError}</Text>
            {evidence.kind === 'indeterminate' &&
            evidence.reason === 'fetch_error' ? (
              <TouchableOpacity onPress={refetch}>
                <Text style={styles.retryText}>Retry</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}
      </View>
    </SetupWizardShell>
    <CategorySelectModal
      visible={categoryModalVisible}
      categories={categories}
      onRequestClose={() => setCategoryModalVisible(false)}
      onSelectCategory={name => {
        handleChangeField('category', name);
        setCategoryModalVisible(false);
      }}
      onAddCategory={name => {
        void addCustomCategory(ownerId, name).then(setCustomCategories);
      }}
    />
    </>
  );
};

const FirstProductStep: React.FC<FirstProductStepProps> = props => {
  const draft = useAppSelector(selectSimplifiedProductDraft);
  // Seed once from the persisted draft; the provider isolates this instance.
  const initialFormData = useMemo(() => draftToFormSeed(draft), []); // eslint-disable-line react-hooks/exhaustive-deps
  const initialItemType = useMemo(() => draftToItemTypeSeed(draft), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <ProductFormProvider
      initialFormData={initialFormData}
      initialPriceType={initialItemType.priceType}>
      <FirstProductStepInner {...props} initialKind={initialItemType.kind} />
    </ProductFormProvider>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    formContent: {
      gap: v(12),
    },
    guardCard: {
      alignItems: 'center',
      gap: v(10),
      padding: s(20),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.borderSecondary,
    },
    existingCard: {
      alignItems: 'center',
      gap: v(10),
      padding: s(20),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.borderSecondary,
    },
    guardTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(21),
      color: theme.colors.text,
      textAlign: 'center',
    },
    guardBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
      textAlign: 'center',
    },
    errorRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      paddingVertical: v(4),
    },
    errorText: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.errorDark,
    },
    retryText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.primary,
    },
  });

export default FirstProductStep;
